/**
 * База знаний (раздел 6.22): список статей с фильтрами, поиск по словам по статьям и нормативной библиотеке,
 * мои дела (проверка, черновики, пересмотр), подписки на изменения и документы, ждущие распознавания.
 */
import {
  KB_SECTIONS,
  type KbArticleItem,
  type KbQueue,
  type KbSearchResult,
  type KbSection,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { FileText, Plus, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Input, Select } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';
import { Tabs } from '../psd/shared';
import { SubscriptionsTab } from './SubscriptionsTab';
import { ArticleBadges, metaQuery, Snippet, sectionTitle } from './shared';

type Tab = 'articles' | 'queue' | 'subscriptions' | 'scans';
interface KbSearch {
  tab?: Tab;
  q?: string;
  section?: string;
  mine?: string;
}

const rowLink =
  'flex min-h-11 flex-col gap-1 border-b border-line px-4 py-3 last:border-b-0 hover:bg-tile/60 focus-visible:bg-tile/60';

function ArticleRow({ a, meta }: { a: KbArticleItem; meta: ReturnType<typeof useMeta> }) {
  return (
    <Link to="/kb/$articleId" params={{ articleId: a.id }} className={rowLink}>
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="num text-[12px] text-ink-2">{a.number}</span>
        <span className="font-medium text-ink [overflow-wrap:anywhere]">{a.title}</span>
        <ArticleBadges a={a} />
      </span>
      <span className="text-[13px] text-ink-2">
        {sectionTitle(meta.data, a.section)}
        {a.ownerName ? ` · владелец ${a.ownerName}` : a.ownerUnit ? ` · ${a.ownerUnit}` : ''}
        {a.nextReviewAt ? ` · пересмотр до ${date(a.nextReviewAt)}` : ''}
      </span>
      {a.summary ? (
        <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{a.summary}</span>
      ) : null}
    </Link>
  );
}

function useMeta() {
  return useQuery(metaQuery);
}

function SearchResults({ q }: { q: string }) {
  const r = useQuery({
    queryKey: ['kb', 'search', q],
    queryFn: () => api<KbSearchResult>('/kb/search', { query: { q } }),
    enabled: q.trim().length >= 2,
  });
  if (r.isPending) return <Spinner label="Ищем" />;
  if (r.error) return <ErrorBox error={r.error} />;
  if (!r.data.hits.length)
    return (
      <EmptyState title="Ничего не найдено">
        Поиск идёт по словам с учётом окончаний, по статьям и по обозначениям и названиям документов
        нормативной библиотеки. Попробуйте другое слово или его часть.
      </EmptyState>
    );
  return (
    <Panel
      title={`Найдено: ${r.data.hits.length}`}
      meta="по словам; поиск по смыслу появится вместе с Pilot"
      bodyClassName="p-0"
    >
      {r.data.hits.map((h) => (
        <Link key={`${h.kind}${h.id}`} to={h.path} className={rowLink}>
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={h.kind === 'article' ? 'info' : 'neutral'}>
              {h.kind === 'article' ? 'Статья' : 'Документ'}
            </Badge>
            <span className="num text-[12px] text-ink-2">{h.code}</span>
            <span className="font-medium text-ink [overflow-wrap:anywhere]">{h.title}</span>
            {h.version ? <span className="text-[12px] text-ink-2">версия {h.version}</span> : null}
            {h.status ? <Badge tone="warning">{h.status}</Badge> : null}
          </span>
          {h.snippet ? (
            <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
              <Snippet text={h.snippet} />
            </span>
          ) : null}
        </Link>
      ))}
    </Panel>
  );
}

function ArticlesTab({ search, setSearch }: { search: KbSearch; setSearch: (p: Partial<KbSearch>) => void }) {
  const meta = useMeta();
  const list = useQuery({
    queryKey: ['kb', 'articles', search.section ?? '', search.mine ?? ''],
    queryFn: () =>
      api<KbArticleItem[]>('/kb/articles', { query: { section: search.section, mine: search.mine } }),
  });
  if (list.isPending) return <Spinner />;
  if (list.error) return <ErrorBox error={list.error} />;
  return list.data.length ? (
    <Panel bodyClassName="p-0">
      {list.data.map((a) => (
        <ArticleRow key={a.id} a={a} meta={meta} />
      ))}
    </Panel>
  ) : (
    <EmptyState
      title="Статей пока нет"
      action={
        meta.data?.canEdit ? (
          <Link
            to="/kb/new"
            className="inline-flex h-11 items-center gap-2 rounded-md bg-accent px-4 font-semibold text-on-accent hover:underline md:h-10"
          >
            <Plus className="size-4" /> Написать первую статью
          </Link>
        ) : undefined
      }
    >
      {search.section || search.mine
        ? 'По этим условиям статей нет. Уберите отбор.'
        : 'Здесь будут регламенты компании, правила расчётов, шаблоны и уроки проектов. Статью пишет сотрудник с правом на документы, публикует её владелец после проверки.'}
      {search.section ? (
        <button
          type="button"
          className="ml-2 underline"
          onClick={() => setSearch({ section: undefined, mine: undefined })}
        >
          Показать все
        </button>
      ) : null}
    </EmptyState>
  );
}

function QueueTab() {
  const meta = useMeta();
  const q = useQuery({ queryKey: ['kb', 'queue'], queryFn: () => api<KbQueue>('/kb/queue') });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  const d = q.data;
  const empty = !d.toReview.length && !d.mine.length && !d.dueReview.length;
  if (empty)
    return (
      <EmptyState title="Дел нет">
        Нет версий, которые ждут вашей проверки, черновиков и статей к пересмотру.
      </EmptyState>
    );
  return (
    <div className="flex flex-col gap-4">
      {d.toReview.length ? (
        <Panel title="Ждут вашей проверки" meta={`${d.toReview.length}`} bodyClassName="p-0">
          {d.toReview.map((a) => (
            <Link key={a.versionId} to="/kb/$articleId" params={{ articleId: a.id }} className={rowLink}>
              <span className="font-medium text-ink [overflow-wrap:anywhere]">
                {a.number} {a.title} · версия {a.version}
              </span>
              <span className="text-[13px] text-ink-2">
                {sectionTitle(meta.data, a.section)} · автор {a.authorName ?? '—'} · отправлена{' '}
                {date(a.submittedAt)}
              </span>
            </Link>
          ))}
        </Panel>
      ) : null}
      {d.dueReview.length ? (
        <Panel title="Пора пересмотреть" meta="вы владелец" bodyClassName="p-0">
          {d.dueReview.map((a) => (
            <ArticleRow key={a.id} a={a} meta={meta} />
          ))}
        </Panel>
      ) : null}
      {d.mine.length ? (
        <Panel title="Мои черновики и возвращённые версии" bodyClassName="p-0">
          {d.mine.map((a) => (
            <Link key={a.versionId} to="/kb/$articleId" params={{ articleId: a.id }} className={rowLink}>
              <span className="font-medium text-ink [overflow-wrap:anywhere]">
                {a.number} {a.title} · версия {a.version}{' '}
                <Badge tone={a.versionStatus === 'rejected' ? 'danger' : 'neutral'}>
                  {a.versionStatus === 'rejected' ? 'Возвращена' : 'Черновик'}
                </Badge>
              </span>
              {a.reviewComment ? <span className="text-[13px] text-ink-2">{a.reviewComment}</span> : null}
            </Link>
          ))}
        </Panel>
      ) : null}
    </div>
  );
}

function ScansTab() {
  const meta = useMeta();
  const q = useQuery({
    queryKey: ['kb', 'ocr-needed'],
    queryFn: () =>
      api<Array<{ releaseId: string; code: string; name: string; ocr: string | null }>>(
        '/kb/norms/ocr-needed',
      ),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorBox error={q.error} />;
  return (
    <div className="flex flex-col gap-4">
      {meta.data && !meta.data.ocr.available ? (
        <div className="rounded-md border border-warning/30 bg-warning-soft px-4 py-3 text-[14px] text-ink">
          На сервере портала не установлено распознавание сканов
          {meta.data.ocr.note ? ` (${meta.data.ocr.note})` : ''}. Эти документы можно скачать, но искать по
          тексту в них нельзя, пока на сервере не появятся tesseract с русским словарём и poppler.
          Распознавание не подменяется.
        </div>
      ) : null}
      {q.data.length ? (
        <Panel title="Документы без текстового слоя" meta={`${q.data.length}`} bodyClassName="p-0">
          {q.data.map((r) => (
            <Link
              key={r.releaseId}
              to="/kb/norms/$releaseId"
              params={{ releaseId: r.releaseId }}
              className={rowLink}
            >
              <span className="font-medium text-ink [overflow-wrap:anywhere]">
                {r.code} {r.name}
              </span>
              <span className="text-[13px] text-ink-2">
                {r.ocr === 'queued'
                  ? 'в очереди на распознавание'
                  : r.ocr === 'done'
                    ? 'распознан'
                    : r.ocr === 'failed'
                      ? 'распознать не удалось'
                      : 'ждёт распознавания'}
              </span>
            </Link>
          ))}
        </Panel>
      ) : (
        <EmptyState title="Сканов, ждущих распознавания, нет">
          Здесь появятся документы библиотеки, у которых нет текстового слоя. Список пополняется, когда
          документ открывают или когда фоновая задача читает его текст.
        </EmptyState>
      )}
    </div>
  );
}

export function KbPage() {
  const search = useSearch({ strict: false }) as KbSearch;
  const navigate = useNavigate();
  const meta = useMeta();
  useCrumbs(undefined);
  const setSearch = (patch: Partial<KbSearch>) =>
    navigate({ to: '/kb', search: (prev: KbSearch) => ({ ...prev, ...patch }), replace: true });
  const tab: Tab = search.tab ?? 'articles';
  const [text, setText] = useState(search.q ?? '');
  // Запрос в адресе обновляется с задержкой: на каждую букву страницу не перезагружаем
  // biome-ignore lint/correctness/useExhaustiveDependencies: setSearch пересоздаётся при каждом показе, запуск — только по тексту и адресу
  useEffect(() => {
    const t = setTimeout(() => {
      const v = text.trim() || undefined;
      if (v !== search.q) setSearch({ q: v });
    }, 350);
    return () => clearTimeout(t);
  }, [text, search.q]);
  const queue = useQuery({
    queryKey: ['kb', 'queue'],
    queryFn: () => api<KbQueue>('/kb/queue'),
    retry: false,
  });
  const todo = (queue.data?.toReview.length ?? 0) + (queue.data?.dueReview.length ?? 0);
  const searching = (search.q ?? '').trim().length >= 2;

  if (meta.error)
    return (
      <Page>
        <ErrorBox error={meta.error} />
      </Page>
    );
  return (
    <Page>
      <PageHeader
        title="База знаний"
        subtitle="Регламенты, правила расчётов, шаблоны и уроки проектов; нормативные документы"
        actions={
          meta.data?.canEdit ? (
            <Link
              to="/kb/new"
              className="inline-flex h-11 items-center gap-2 rounded-md bg-accent px-4 text-[14px] font-semibold text-on-accent hover:underline md:h-10"
            >
              <Plus className="size-4" /> Новая статья
            </Link>
          ) : null
        }
      />
      <div className="relative mb-4">
        <Search
          className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3"
          aria-hidden
        />
        <Input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Найти по словам в статьях и нормативных документах"
          aria-label="Поиск по базе знаний"
          className="h-11 pl-9 md:h-10"
        />
      </div>
      {searching ? (
        <SearchResults q={search.q!.trim()} />
      ) : (
        <>
          <Tabs
            value={tab}
            onChange={(v) => setSearch({ tab: v === 'articles' ? undefined : v })}
            items={[
              { value: 'articles' as const, label: 'Статьи' },
              {
                value: 'queue' as const,
                label: todo ? `Мои дела (${todo})` : 'Мои дела',
              },
              { value: 'subscriptions' as const, label: 'Подписки' },
              { value: 'scans' as const, label: 'Сканы' },
            ]}
          />
          {tab === 'articles' ? (
            <div className="mb-4 flex flex-col gap-2 md:flex-row">
              <Select
                aria-label="Раздел"
                value={search.section ?? ''}
                onChange={(e) => setSearch({ section: e.target.value || undefined })}
                className="h-11 md:h-10 md:w-72"
              >
                <option value="">Все разделы</option>
                {(Object.keys(KB_SECTIONS) as KbSection[]).map((s) => (
                  <option key={s} value={s}>
                    {KB_SECTIONS[s]}
                  </option>
                ))}
              </Select>
              <label className="flex h-11 cursor-pointer items-center gap-2 px-1 text-[14px] md:h-10">
                <input
                  type="checkbox"
                  className="size-5 md:size-4"
                  checked={search.mine === '1'}
                  onChange={(e) => setSearch({ mine: e.target.checked ? '1' : undefined })}
                />
                Только мои (я владелец)
              </label>
            </div>
          ) : null}
          {tab === 'articles' ? <ArticlesTab search={search} setSearch={setSearch} /> : null}
          {tab === 'queue' ? <QueueTab /> : null}
          {tab === 'subscriptions' ? <SubscriptionsTab /> : null}
          {tab === 'scans' ? <ScansTab /> : null}
        </>
      )}
      {!searching && meta.data && !meta.data.pilot.available ? (
        <p className="mt-6 flex items-start gap-2 text-[13px] text-ink-2">
          <FileText className="mt-0.5 size-4 shrink-0" aria-hidden />
          Pilot пока не подключён: поиск идёт по словам, ответов с пересказом статей нет.{' '}
        </p>
      ) : null}
    </Page>
  );
}
